import styled from '@emotion/styled';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from 'react-query';

const SearchInputContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    background: white;
    border-radius: 6px;
    border: 1px solid var(--Blacks-300, #dbdbdb);
    width: 280px;
    padding: 8px 12px;
    margin-bottom: 24px;
    height: 44px;
`;

const SearchInput = styled.input`
    background: none;
    border: none;
    outline: none;
    padding: 0;
    flex: 1;
    color: ${({ theme }) => theme.colors.black[500]};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.28px;

    &:focus {
        outline: none;
    }

    &::placeholder {
        color: ${({ theme }) => theme.colors.black[300]};
    }
`;

const BackgroundsGrid = styled.div`
    gap: 11px;
    grid-template-columns: repeat(auto-fill, minmax(196px, calc((100% - 33px) / 4)));
    display: grid;
    width: 100%;

    @media (max-width: 912px) {
        grid-template-columns: repeat(auto-fill, minmax(196px, calc((100% - 11px) / 2)));
    }
`;

const BackgroundImageCard = styled.div<{ selected: boolean }>`
    aspect-ratio: 16/9;
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 10px;
    overflow: hidden;
    border: ${({ selected, theme }) => (selected ? `3px solid ${theme.colors.primary.main}` : '1px solid transparent')};
    transition: border-color 0.2s ease;

    &:hover {
        border-color: ${({ theme }) => theme.colors.primary.main};
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        display: block;
    }
`;

const DefaultBackgroundsContainer = styled.div<{ maxHeight: string }>`
    max-height: ${({ maxHeight }) => maxHeight};
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    ::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    gap: 12px;
    color: ${({ theme }) => theme.colors.black[500]};
`;

interface UnsplashImage {
    id: string;
    urls: {
        raw: string;
        full: string;
        regular: string;
        small: string;
        thumb: string;
        small_s3: string;
    };
    alt_description: string | null;
}

interface UnsplashResponse {
    total: number;
    total_pages: number;
    results: UnsplashImage[];
}

interface Props {
    value: string;
    onChange: (value: string) => void;
}

const fetchUnsplashImages = async (query: string, page: number = 1, perPage: number = 20): Promise<UnsplashResponse> => {
    const response = await fetch(
        `https://api.polotno.com/api/get-unsplash?query=${encodeURIComponent(query || 'background')}&per_page=${perPage}&page=${page}`
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch images: ${response.statusText}`);
    }

    return response.json();
};

export const DefaultBackgrounds = observer(({ value, onChange }: Props) => {
    const [searchInput, setSearchInput] = useState('');
    const [query, setQuery] = useState('');
    const { ref: lastElementRef, inView } = useInView({ threshold: 0, rootMargin: '200px' });

    const debouncedSetQuery = useMemo(
        () =>
            debounce((newQuery: string) => {
                setQuery(newQuery);
            }, 500),
        []
    );

    const handleSearchChange = (newQuery: string) => {
        setSearchInput(newQuery);
        debouncedSetQuery(newQuery);
    };

    const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteQuery(
        ['unsplash-backgrounds', query],
        ({ pageParam = 1 }) => fetchUnsplashImages(query, pageParam),
        {
            getNextPageParam: (lastPage, pages) => {
                const currentPage = pages.length;
                return currentPage < lastPage.total_pages ? currentPage + 1 : undefined;
            },
            staleTime: 1000 * 60 * 5,
            cacheTime: 1000 * 60 * 10,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );

    const images = data?.pages.flatMap(page => page.results) ?? [];

    const handleImageClick = (imageUrl: string) => {
        onChange(imageUrl);
    };

    useEffect(() => {
        if (inView && hasNextPage && !isLoading) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isLoading, fetchNextPage]);

    useEffect(() => {
        return () => {
            debouncedSetQuery.cancel();
        };
    }, [debouncedSetQuery]);

    return (
        <DefaultBackgroundsContainer maxHeight={`calc(100% - ${19 + 16}px)`}>
            <SearchInputContainer>
                <SearchIcon sx={{ color: 'inherit', fontSize: 20 }} />
                <SearchInput
                    type="text"
                    placeholder="Search backgrounds..."
                    value={searchInput}
                    onChange={e => handleSearchChange(e.target.value)}
                />
            </SearchInputContainer>

            <BackgroundsGrid>
                {images.map((image, index) => (
                    <BackgroundImageCard
                        key={image.id + index}
                        ref={index === images.length - 1 ? lastElementRef : null}
                        selected={value === image.urls.regular}
                        onClick={() => handleImageClick(image.urls.regular)}>
                        <img src={image.urls.small} alt={image.alt_description || 'Background image'} loading="lazy" />
                    </BackgroundImageCard>
                ))}
            </BackgroundsGrid>

            {isLoading && (
                <LoadingContainer>
                    <CircularProgress size={20} />
                </LoadingContainer>
            )}
        </DefaultBackgroundsContainer>
    );
});
